/**
 * Processing Activity — detail / editor. The GDPR Article 30 record.
 *
 * Routes
 *   /processing-activities/new             mints an empty draft server-side then redirects,
 *                                          so there is never an "unsaved new record" state.
 *   /processing-activities/:activityId     the editor for that record.
 *
 * A single editable document rather than a wizard. A ROPA row is assembled over time by
 * several people and returned to whenever processing changes — a step-by-step intake flow
 * would be wrong for a record with no end state. Everything is held in a local draft and
 * written on Save; only the status transitions write immediately, because they change what
 * the register asserts about the activity.
 *
 * Status is a server-side state machine (draft → active → retired). It is not a writable
 * attribute and never appears in the save payload. Unlike a DPIA there is deliberately **no
 * read-only lock on `active`**: Article 30 requires the record to be *current*, so an active
 * activity must stay editable. Retiring it is how you stop it being asserted.
 *
 * Several fields are conditional, and the server clears them rather than storing something
 * that no longer applies — joint controllers when the role changes, the legitimate-interests
 * assessment when the basis changes, the Article 9 condition when the last special-category
 * flag is cleared. The editor mirrors that so the two never disagree on screen.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';

import { PageHeader } from '../../components/PageHeader';
import { RichTextField, isRichTextEmpty } from '../../components/RichTextField';
import { newRowId, useRepeatableRows, RepeatableRowCard } from '../../components/RepeatableRows';
import type { RowAccent } from '../../components/RepeatableRows';

import { processingActivityApi, peopleApi, departmentsApi, documentApi } from '../../services/api';
import type {
  ActivityStatus,
  DataField,
  Department,
  Person,
  ProcessingActivity,
  ProcessorRole,
  RetentionRule,
  TransferMechanism,
  TransferRecord,
} from '../../types/models';
import {
  ACTIVITY_ACTION_LABELS,
  ACTIVITY_STATUS_LABELS,
  ACTIVITY_STATUS_TONE,
  ACTIVITY_STATUS_TRANSITIONS,
  DATA_CATEGORIES,
  DATA_CATEGORY_LABELS,
  DATA_SENSITIVITY_ANSWERS,
  DATA_SENSITIVITY_HINT,
  DATA_SENSITIVITY_LABELS,
  DATA_SENSITIVITY_TONE,
  DATA_SUBJECT_CATEGORIES,
  DATA_SUBJECT_CATEGORY_LABELS,
  DPIA_VERDICT_LABELS,
  DPIA_VERDICT_TONE,
  LEGAL_BASIS_OPTIONS,
  PROCESSOR_ROLES,
  PROCESSOR_ROLE_DESCRIPTIONS,
  PROCESSOR_ROLE_LABELS,
  RECIPIENT_CATEGORIES,
  RECIPIENT_CATEGORY_LABELS,
  RESIDUAL_RISK_LABELS,
  RESIDUAL_RISK_TONE,
  SENSITIVITY_LABELS,
  SENSITIVITY_LEVELS,
  SUBJECT_VOLUME_BANDS,
  SUBJECT_VOLUME_LABELS,
  TRANSFER_MECHANISMS,
  TRANSFER_MECHANISM_LABELS,
  TRANSFER_MECHANISM_TONE,
  hasVulnerableSubjects,
} from '../../constants/privacy';
import type { ResidualRiskLevel } from '../../constants/privacy';
import { reactSelectStyles } from '../../utils/reactSelectStyles';
import { formatDate } from '../../utils/format';
import { describeApiError } from '../../utils/apiError';
import { useToast } from '../../hooks/useToast';
import { useSwal } from '../../hooks/useSwal';

// ─── Shared bits ──────────────────────────────────────────────────────────────

interface SelectOption { value: string; label: string }

const Spinner: React.FC = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

const SectionCard: React.FC<{
  title: string;
  icon: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ title, icon, hint, children }) => (
  <div className="np-card">
    <div className="np-card-head">
      <span className="np-icon-badge np-icon-badge-sm np-tone-primary"><i className={icon}></i></span>
      <div>
        <h2 className="mb-0 fw-semibold">{title}</h2>
        {hint && <div className="np-sub">{hint}</div>}
      </div>
    </div>
    <div className="np-card-body">{children}</div>
  </div>
);

/**
 * Only the moves that change what the register *asserts* get a confirm. Activating says
 * "this is our official record of this processing"; retiring withdraws that.
 */
const TRANSITION_CONFIRM: Partial<Record<ActivityStatus, { title: string; text: string }>> = {
  active: {
    title: 'Activate this processing activity?',
    text: 'It becomes part of your official Article 30 record. It stays editable — Article 30 requires the record to be current — but it will be included when the register is produced.',
  },
  retired: {
    title: 'Retire this processing activity?',
    text: 'It will no longer be asserted as current processing. The record is kept, because Article 30 may still require you to produce it for a past period.',
  },
  draft: {
    title: 'Return this activity to draft?',
    text: 'It will be excluded from the active Article 30 record until you activate it again.',
  },
};

// ─── Editor ───────────────────────────────────────────────────────────────────

const ActivityEditor: React.FC<{ record: ProcessingActivity }> = ({ record }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const [draft, setDraft] = useState<ProcessingActivity>(record);
  const [dirty, setDirty] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const update = useCallback((changes: Partial<ProcessingActivity>) => {
    setDraft((prev) => ({ ...prev, ...changes }));
    setDirty(true);
  }, []);

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });
  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  // Residual risk is owned by the linked DPIA document - fetched read-time here rather than
  // mirrored onto the activity, so it stays in sync without a new cross-lambda IAM grant.
  const { data: latestDpiaDoc } = useQuery<{ residualRiskLevel?: ResidualRiskLevel }>({
    queryKey: ['document', draft.latestDpiaId],
    queryFn: async () => {
      const data = await documentApi.getOne(draft.latestDpiaId);
      return (data as any).document || data;
    },
    enabled: !!draft.latestDpiaId,
  });
  const latestDpiaResidualRisk = latestDpiaDoc?.residualRiskLevel;

  const peopleOptions: SelectOption[] = useMemo(
    () => people.map((p) => ({ value: p.personId, label: p.personName })),
    [people],
  );
  const departmentOptions: SelectOption[] = useMemo(
    () => departments.map((d) => ({ value: d.departmentId, label: d.name })),
    [departments],
  );

  /** Resolve a stored id back to an option, tolerating a deleted person/department. */
  const optionFor = (options: SelectOption[], id: string): SelectOption | null => {
    if (!id) return null;
    return options.find((o) => o.value === id) ?? { value: id, label: id };
  };

  const multiOptions = (values: readonly string[], labels: Record<string, string>): SelectOption[] =>
    values.map((v) => ({ value: v, label: labels[v] ?? v }));

  const selectedMulti = (ids: string[], labels: Record<string, string>): SelectOption[] =>
    ids.map((id) => ({ value: id, label: labels[id] ?? id }));

  // ── Conditional visibility, mirroring the server's clearing rules ──────────
  const isJoint = draft.role === 'joint-controller';
  const usesLegitimateInterests = draft.legalBasis === 'legitimate-interests';
  // 'customer_controlled' counts — a non-empty string is truthy regardless of value.
  const hasSpecialCategory = (draft.dataFields ?? []).some((f) => f.isSpecialCategory);
  const vulnerableFromCategories = hasVulnerableSubjects(draft.dataSubjectCategories ?? []);

  // ── Repeatable sections ───────────────────────────────────────────────────
  // DataField, TransferRecord and RetentionRule all already key on `id`, so unlike the
  // minutes editor no mirror id is needed.

  // seedWhenEmpty: false on all three - each is a genuinely optional Article 30 category (an
  // activity can legitimately have no international transfers, no bespoke retention rule, or
  // no itemised data field beyond what's already implied), and the server-side normaliser for
  // each drops blank entries on save. Left at the hook's own default (true), a genuinely empty
  // list would never stay saved-and-clean - see seedWhenEmpty's own doc comment in
  // RepeatableRows.tsx for the exact loop this caused (confirmed live on Transfers).
  const dataRows = useRepeatableRows<DataField>({
    stored: draft.dataFields,
    makeBlank: () => ({
      id: newRowId(), name: '', category: 'personal',
      isPersonalData: 'yes', isSpecialCategory: false,
      sensitivity: 'internal', processingPurpose: '', dataSource: '',
    }),
    isBlank: (row) => !row.name.trim() && !row.processingPurpose.trim() && !row.dataSource.trim(),
    onChange: (rows) => update({ dataFields: rows }),
    seedWhenEmpty: false,
  });

  const transferRows = useRepeatableRows<TransferRecord>({
    stored: draft.transfers,
    makeBlank: () => ({
      id: newRowId(), country: '', recipient: '', mechanism: 'none', safeguards: '',
    }),
    isBlank: (row) => !row.country.trim() && !row.recipient.trim(),
    onChange: (rows) => update({ transfers: rows }),
    seedWhenEmpty: false,
  });

  const retentionRows = useRepeatableRows<RetentionRule>({
    stored: draft.retention,
    makeBlank: () => ({ id: newRowId(), dataCategory: 'personal', period: '', trigger: '' }),
    isBlank: (row) => !row.period.trim() && !row.trigger.trim(),
    onChange: (rows) => update({ retention: rows }),
    seedWhenEmpty: false,
  });

  // ── Persistence ───────────────────────────────────────────────────────────
  // `status`, `latestDpiaId`, `dpiaVerdict` and `lastScreenedAt` are deliberately absent:
  // the server owns them and ignores them on a PUT, so sending them would be noise that
  // pollutes the audit log's changedKeys.
  const writablePayload = (d: ProcessingActivity): Partial<ProcessingActivity> => ({
    name: d.name, role: d.role, jointControllers: d.jointControllers, description: d.description,
    purposes: d.purposes, legalBasis: d.legalBasis,
    legitimateInterestsAssessment: d.legitimateInterestsAssessment,
    specialCategoryCondition: d.specialCategoryCondition,
    dataSubjectCategories: d.dataSubjectCategories,
    isVulnerableGroup: d.isVulnerableGroup,
    dataSubjectVolume: d.dataSubjectVolume,
    dataFields: d.dataFields,
    recipientCategories: d.recipientCategories,
    processors: d.processors,
    transfers: d.transfers,
    retention: d.retention,
    securityMeasures: d.securityMeasures,
    systemIds: d.systemIds,
    ownerId: d.ownerId, departmentId: d.departmentId,
  });

  const saveMutation = useMutation({
    mutationFn: () => processingActivityApi.update(draft.activityId, writablePayload(draft)),
    onSuccess: (saved) => {
      setDraft(saved);
      setDirty(false);
      queryClient.setQueryData(['processingActivity', saved.activityId], saved);
      queryClient.invalidateQueries({ queryKey: ['list', '/processing-activities'] });
      toast.success('Processing activity updated.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not save this processing activity.')),
  });

  const transitionMutation = useMutation({
    mutationFn: (status: ActivityStatus) => processingActivityApi.transition(draft.activityId, status),
    onSuccess: (saved) => {
      setDraft(saved);
      setDirty(false);
      queryClient.setQueryData(['processingActivity', saved.activityId], saved);
      queryClient.invalidateQueries({ queryKey: ['list', '/processing-activities'] });
    },
    onError: (err) =>
      toast.error(describeApiError(err, 'Could not change the status of this activity.')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => processingActivityApi.delete(draft.activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list', '/processing-activities'] });
      toast.success('Processing activity deleted.');
      navigate('/processing-activities');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not delete this processing activity.')),
  });

  const screenDpiaMutation = useMutation({
    mutationFn: () => processingActivityApi.screenForDpia(draft.activityId),
    onSuccess: (saved) => {
      setDraft(saved);
      queryClient.setQueryData(['processingActivity', saved.activityId], saved);
      queryClient.invalidateQueries({ queryKey: ['list', '/processing-activities'] });
      toast.success(
        saved.dpiaVerdict === 'required'
          ? 'DPIA required — a draft DPIA has been created and linked.'
          : 'Screened: no DPIA required based on the recorded fields.',
      );
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not screen this activity for a DPIA.')),
  });

  const handleTransition = async (target: ActivityStatus) => {
    // Unsaved edits would not be seen by the server's completeness check, so it would
    // validate a stale record and either pass wrongly or fail confusingly.
    if (dirty) {
      toast.error('Save your changes before changing the status.');
      return;
    }
    const confirm = TRANSITION_CONFIRM[target];
    if (confirm) {
      const ok = await swal.confirm(confirm.title, confirm.text);
      if (!ok) return;
    }
    transitionMutation.mutate(target);
  };

  const handleScreenForDpia = async () => {
    if (dirty) {
      toast.error('Save your changes before screening for a DPIA.');
      return;
    }
    const ok = await swal.confirm(
      'Screen for DPIA?',
      'This checks the recorded fields (special category data, vulnerable subjects, large-scale volume, international transfers) and records a verdict. It is a lightweight heuristic, not a full Article 35 assessment.',
    );
    if (!ok) return;
    screenDpiaMutation.mutate();
  };

  const handleDelete = async () => {
    const ok = await swal.confirmDelete(draft.name || draft.activityId);
    if (ok) deleteMutation.mutate();
  };

  // Warn on navigating away with unsaved work. The register is assembled over long
  // sittings and a lost paragraph of purposes is genuinely costly.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const nextStatuses = ACTIVITY_STATUS_TRANSITIONS[draft.status] ?? [];
  const verdictKey = (draft.dpiaVerdict || '') as keyof typeof DPIA_VERDICT_LABELS;

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const { htmlToPdf } = await import('../../utils/pdfExport');
      const ACCENT = '#4f46e5';
      const cs = (s: string) =>
        s ? String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : '—';

      const sectionHead = (label: string, clause: string) =>
        `<div style="margin:28px 0 12px 0;">
          <h2 style="font-size:16px;font-weight:700;color:#111827;margin:0 0 2px 0;">${cs(label)}</h2>
          <div style="font-size:11px;color:#6b7280;">${cs(clause)}</div>
        </div>`;

      const tableHtml = (items: any[], cols: { key: string; label: string; render?: (row: any) => string }[]) => {
        if (!items?.length) return '<p style="font-size:12px;color:#9ca3af;font-style:italic;">None recorded</p>';
        const hdr = cols.map((c) =>
          `<th style="padding:6px 10px;background:#f9fafb;font-size:11px;font-weight:700;color:#374151;text-align:left;border:1px solid #e5e7eb;">${cs(c.label)}</th>`
        ).join('');
        const rows = items.map((row) =>
          `<tr>${cols.map((c) =>
            `<td style="padding:6px 10px;border:1px solid #e5e7eb;font-size:12px;color:#374151;vertical-align:top;">${c.render ? c.render(row) : cs(row[c.key] || '')}</td>`
          ).join('')}</tr>`
        ).join('');
        return `<table style="width:100%;border-collapse:collapse;"><thead><tr>${hdr}</tr></thead><tbody>${rows}</tbody></table>`;
      };

      const prose = (html: string) =>
        isRichTextEmpty(html)
          ? '<p style="font-size:12px;color:#9ca3af;font-style:italic;">No content recorded</p>'
          : `<div style="font-size:13px;color:#111827;line-height:1.7;">${html}</div>`;

      const statusMeta: Record<string, { bg: string; color: string }> = {
        draft: { bg: '#fef3c7', color: '#d97706' },
        active: { bg: '#dcfce7', color: '#15803d' },
        retired: { bg: '#f3f4f6', color: '#6b7280' },
      };
      const sm = statusMeta[draft.status] ?? { bg: '#f3f4f6', color: ACCENT };
      const ownerName = optionFor(peopleOptions, draft.ownerId)?.label || '—';
      const departmentName = optionFor(departmentOptions, draft.departmentId)?.label || '—';
      const legalBasisLabel = LEGAL_BASIS_OPTIONS.find((o) => o.value === draft.legalBasis)?.label || '—';

      const html = `
<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:860px;padding:40px;">

  <div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:24px;">
    <div style="font-size:11px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">GDPR Article 30 — Record of Processing Activities</div>
    <h1 style="font-size:24px;font-weight:800;color:#111827;margin:0 0 14px 0;">${cs(draft.name || draft.activityId)}</h1>
    <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
      <span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:${sm.color};background:${sm.bg};">${cs(ACTIVITY_STATUS_LABELS[draft.status])}</span>
      <span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:${DPIA_VERDICT_TONE[verdictKey] === 'danger' ? '#b91c1c' : '#374151'};background:#f3f4f6;">${cs(DPIA_VERDICT_LABELS[verdictKey])}</span>
      <span style="font-size:12px;color:#6b7280;">${cs(draft.activityId)} · Owner: ${cs(ownerName)} · Dept: ${cs(departmentName)}</span>
    </div>
  </div>

  ${sectionHead('Role & Legal Basis', 'Article 30(1)(a) / Article 6')}
  <div style="font-size:13px;line-height:1.9;">
    <div><strong>Role:</strong> ${cs(PROCESSOR_ROLE_LABELS[draft.role])}</div>
    ${draft.jointControllers ? `<div><strong>Joint controllers:</strong> ${cs(draft.jointControllers)}</div>` : ''}
    <div><strong>Legal basis:</strong> ${cs(legalBasisLabel)}</div>
    ${draft.specialCategoryCondition ? `<div><strong>Article 9 condition:</strong> ${cs(draft.specialCategoryCondition)}</div>` : ''}
  </div>

  ${sectionHead('Purposes of Processing', 'Article 30(1)(b)')}
  ${prose(draft.purposes)}

  ${sectionHead('Data Subjects & Fields', 'Article 30(1)(c)')}
  <div style="font-size:12px;color:#374151;margin-bottom:10px;">
    Categories: ${draft.dataSubjectCategories.length ? draft.dataSubjectCategories.map((c) => cs(DATA_SUBJECT_CATEGORY_LABELS[c] ?? c)).join(', ') : '—'}
    · Volume: ${cs(SUBJECT_VOLUME_LABELS[draft.dataSubjectVolume] ?? draft.dataSubjectVolume)}
    ${draft.isVulnerableGroup ? ' · <span style="color:#b91c1c;">Vulnerable group</span>' : ''}
  </div>
  ${tableHtml(draft.dataFields, [
    { key: 'name', label: 'Field' },
    { key: 'category', label: 'Category', render: (r: any) => cs((DATA_CATEGORY_LABELS as Record<string, string>)[r.category] ?? r.category) },
    { key: 'isSpecialCategory', label: 'Special category', render: (r: any) => (r.isSpecialCategory ? 'Yes' : 'No') },
  ])}

  ${sectionHead('Recipients & Transfers', 'Article 30(1)(d)–(e)')}
  <div style="font-size:12px;color:#374151;margin-bottom:10px;">
    Recipient categories: ${draft.recipientCategories.length ? draft.recipientCategories.map((c) => cs(RECIPIENT_CATEGORY_LABELS[c] ?? c)).join(', ') : '—'}
  </div>
  ${tableHtml(draft.transfers, [
    { key: 'country', label: 'Country' },
    { key: 'recipient', label: 'Recipient' },
    { key: 'mechanism', label: 'Mechanism', render: (r: any) => cs((TRANSFER_MECHANISM_LABELS as Record<string, string>)[r.mechanism] ?? r.mechanism) },
    { key: 'safeguards', label: 'Safeguards' },
  ])}

  ${sectionHead('Retention', 'Article 30(1)(f)')}
  ${tableHtml(draft.retention, [
    { key: 'dataCategory', label: 'Data category', render: (r: any) => cs((DATA_CATEGORY_LABELS as Record<string, string>)[r.dataCategory] ?? r.dataCategory) },
    { key: 'period', label: 'Period' },
    { key: 'trigger', label: 'Trigger' },
  ])}

  ${sectionHead('Security Measures', 'Article 30(1)(g) / Article 32')}
  ${prose(draft.securityMeasures)}

  ${sectionHead('DPIA Screening', 'Article 35')}
  <div style="font-size:13px;line-height:1.9;">
    <div><strong>Verdict:</strong> ${cs(DPIA_VERDICT_LABELS[verdictKey])}</div>
    ${draft.lastScreenedAt ? `<div><strong>Last screened:</strong> ${formatDate(draft.lastScreenedAt)}</div>` : ''}
    ${draft.latestDpiaId ? `<div><strong>Linked DPIA:</strong> ${cs(draft.latestDpiaId)}</div>` : ''}
    ${latestDpiaResidualRisk ? `<div><strong>Residual risk:</strong> ${cs(RESIDUAL_RISK_LABELS[latestDpiaResidualRisk])}</div>` : ''}
  </div>

  <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;">
    <span style="font-size:11px;color:#9ca3af;">${cs(draft.activityId)}</span>
    <span style="font-size:11px;color:#9ca3af;">Generated ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
  </div>
</div>`;

      await htmlToPdf(html, `${draft.activityId}-processing-activity.pdf`);
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-database-2-line"
        title={draft.name || draft.activityId}
        subtitle={`${draft.activityId} · updated ${formatDate(draft.updatedAt)}`}
        colour={ACTIVITY_STATUS_TONE[draft.status]}
        breadcrumbs={[
          { label: 'Processing Activities', to: '/processing-activities' },
          { label: draft.activityId },
        ]}
        actions={
          <>
            <button
              className="np-btn np-btn-primary"
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
            >
              <i className="ri-save-line"></i>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {nextStatuses.map((s) => (
              <button
                key={s}
                className={s === 'retired' ? 'np-btn np-btn-ghost-danger' : 'np-btn np-btn-ghost'}
                onClick={() => handleTransition(s)}
                // Also disabled while the save itself is still in flight, not just while a
                // transition is - handleTransition's own dirty-check reads true until the save's
                // onSuccess actually lands, so clicking Save then immediately Activate (before
                // that round trip completes) hit "Save your changes before changing the status"
                // even though Save had already been clicked. Confirmed live as a real gap.
                disabled={transitionMutation.isPending || saveMutation.isPending}
              >
                <i className={s === 'active' ? 'ri-shield-check-line' : 'ri-archive-line'}></i>
                {ACTIVITY_ACTION_LABELS[s]}
              </button>
            ))}
            <button
              className="np-btn np-btn-ghost"
              onClick={handleScreenForDpia}
              disabled={screenDpiaMutation.isPending || saveMutation.isPending}
              title="Screen for DPIA"
            >
              {screenDpiaMutation.isPending
                ? <><span className="spinner-border spinner-border-sm me-1" />Screening…</>
                : <><i className="ri-radar-line"></i>Screen for DPIA</>}
            </button>
            <button
              className="np-btn np-btn-ghost"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              title="Export to PDF"
            >
              {exportingPdf
                ? <><span className="spinner-border spinner-border-sm me-1" />Exporting…</>
                : <><i className="ri-file-pdf-line"></i>Export PDF</>}
            </button>
            <button className="np-btn np-btn-ghost-danger" onClick={handleDelete}>
              <i className="ri-delete-bin-line"></i>Delete
            </button>
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/processing-activities')}>
              <i className="ri-arrow-left-line"></i>Back
            </button>
          </>
        }
      />

      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className={`np-chip np-tone-${ACTIVITY_STATUS_TONE[draft.status]}`}>
          {ACTIVITY_STATUS_LABELS[draft.status]}
        </span>
        {draft.latestDpiaId ? (
          <Link to={`/documents/${draft.latestDpiaId}`} className={`np-chip np-tone-${DPIA_VERDICT_TONE[verdictKey] ?? 'neutral'}`}>
            {DPIA_VERDICT_LABELS[verdictKey] ?? 'Not screened'}
          </Link>
        ) : (
          <span className={`np-chip np-tone-${DPIA_VERDICT_TONE[verdictKey] ?? 'neutral'}`}>
            {DPIA_VERDICT_LABELS[verdictKey] ?? 'Not screened'}
          </span>
        )}
        {latestDpiaResidualRisk && (
          <span className={`np-chip np-tone-${RESIDUAL_RISK_TONE[latestDpiaResidualRisk]}`}>
            Residual risk: {RESIDUAL_RISK_LABELS[latestDpiaResidualRisk]}
          </span>
        )}
        {hasSpecialCategory && (
          <span className="np-chip np-tone-danger">
            <i className="ri-alert-line"></i>Special category data
          </span>
        )}
        {draft.sourceUseCaseId && (
          <Link to={`/ai-use-cases/${draft.sourceUseCaseId}`} className="np-chip np-tone-info">
            <i className="ri-magic-line"></i>Drafted from {draft.sourceUseCaseId}
          </Link>
        )}
        {dirty && <span className="np-chip np-tone-warning">Unsaved changes</span>}
      </div>

      {/* Readiness checklist - only for an AI-drafted record still in draft, since these are
          exactly the fields _draft_processing_activity_attrs (lambda/prod-nimbus-ai-use-cases)
          never sets at all: department, specialCategoryCondition, dataSubjectVolume and
          isVulnerableGroup stay at their default_attributes() placeholder forever, and every
          retention rule it drafts has trigger hardcoded to '' since AiUseCase only tracks one
          free-text retention description, not per-category triggers. A manually-created record
          has no sourceUseCaseId and never shows this - a human who typed those defaults chose
          them deliberately. */}
      {draft.sourceUseCaseId && draft.status === 'draft' && (() => {
        const items: string[] = [];
        if (!draft.departmentId) items.push('Department has not been set');
        if (hasSpecialCategory && !draft.specialCategoryCondition) items.push('Special category data is present but no Article 9 condition is recorded');
        if (!draft.dataSubjectVolume) items.push('Data subject volume has not been set');
        // Unconditional before this fix - every sibling item here checks the field it's about,
        // this one never did, so toggling Vulnerable data subjects on could never clear it.
        if (!draft.isVulnerableGroup) items.push('Confirm whether this involves a vulnerable group (children, employees, patients)');
        (draft.retention ?? []).forEach((r) => {
          if (!r.trigger) items.push(`Retention trigger for "${r.dataCategory}" data has not been set`);
        });
        return items.length > 0 ? (
          <div className="np-banner np-tone-warning mb-0 mt-3">
            <i className="ri-list-check-3"></i>
            <div>
              <strong>Needs attention before this leaves draft</strong>
              <ul className="mb-0">
                {items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>
        ) : null;
      })()}

      {/* 1 — Identity and role */}
      <SectionCard
        title="Identity and role"
        icon="ri-id-card-line"
        hint="Who is responsible for this processing, and in what capacity"
      >
        <div className="np-row2">
          <div className="np-field">
            <label htmlFor="pa-name">Name of the processing activity <span className="text-danger">*</span></label>
            <input
              id="pa-name"
              className="np-input"
              value={draft.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="e.g. Recruitment and candidate assessment"
            />
          </div>
          <div className="np-field">
            <label htmlFor="pa-role">Our role</label>
            <select
              id="pa-role"
              className="np-select"
              value={draft.role}
              onChange={(e) => update({ role: e.target.value as ProcessorRole })}
            >
              {PROCESSOR_ROLES.map((r) => (
                <option key={r} value={r}>{PROCESSOR_ROLE_LABELS[r]}</option>
              ))}
            </select>
            <div className="np-hint">{PROCESSOR_ROLE_DESCRIPTIONS[draft.role]}</div>
          </div>
        </div>

        {isJoint && (
          <div className="np-field">
            <label htmlFor="pa-joint">Joint controllers</label>
            <input
              id="pa-joint"
              className="np-input"
              value={draft.jointControllers}
              onChange={(e) => update({ jointControllers: e.target.value })}
              placeholder="Name the other organisation(s) you jointly determine this processing with"
            />
            <div className="np-hint">
              Article 26 requires an arrangement between joint controllers setting out who does what.
            </div>
          </div>
        )}

        <div className="np-row2">
          <div className="np-field">
            <label htmlFor="pa-owner">Owner</label>
            <Select
              inputId="pa-owner"
              options={peopleOptions}
              value={optionFor(peopleOptions, draft.ownerId)}
              onChange={(opt) => update({ ownerId: opt?.value ?? '' })}
              placeholder="Who is accountable for this processing?"
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              styles={reactSelectStyles()}
            />
          </div>
          <div className="np-field">
            <label htmlFor="pa-dept">Department</label>
            <Select
              inputId="pa-dept"
              options={departmentOptions}
              value={optionFor(departmentOptions, draft.departmentId)}
              onChange={(opt) => update({ departmentId: opt?.value ?? '' })}
              placeholder="Which department carries this out?"
              isClearable
              isSearchable
              menuPortalTarget={document.body}
              styles={reactSelectStyles()}
            />
          </div>
        </div>

        <div className="np-field">
          <label>Description</label>
          <RichTextField
            value={draft.description}
            onChange={(html) => update({ description: html })}
            placeholder="A short description of what this processing involves in practice."
          />
        </div>
      </SectionCard>

      {/* 2 — Purpose and lawful basis */}
      <SectionCard
        title="Purpose and lawful basis"
        icon="ri-scales-3-line"
        hint="Article 30(1)(b) and Article 6"
      >
        <div className="np-field">
          <label>Purposes of the processing <span className="text-danger">*</span></label>
          <RichTextField
            value={draft.purposes}
            onChange={(html) => update({ purposes: html })}
            placeholder="Why is this personal data processed? Be specific — “business purposes” is not a purpose."
          />
          {isRichTextEmpty(draft.purposes) && (
            <div className="np-hint">Required before this activity can be activated.</div>
          )}
        </div>

        <div className="np-field">
          <label htmlFor="pa-basis">Lawful basis <span className="text-danger">*</span></label>
          <select
            id="pa-basis"
            className="np-select"
            value={draft.legalBasis}
            onChange={(e) => update({ legalBasis: e.target.value })}
          >
            <option value="">Select a lawful basis…</option>
            {LEGAL_BASIS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="np-hint">
            {LEGAL_BASIS_OPTIONS.find((o) => o.value === draft.legalBasis)?.description
              ?? 'Every processing activity needs one of the six Article 6 bases.'}
          </div>
        </div>

        {usesLegitimateInterests && (
          <div className="np-field">
            <label>Legitimate interests assessment <span className="text-danger">*</span></label>
            <RichTextField
              value={draft.legitimateInterestsAssessment}
              onChange={(html) => update({ legitimateInterestsAssessment: html })}
              placeholder="The three-part test: what the interest is, why the processing is necessary for it, and why it is not overridden by the data subject’s rights."
            />
            <div className="np-hint">
              Required when relying on Article 6(1)(f) — the balancing test has to be recorded, not
              just performed.
            </div>
          </div>
        )}

        {hasSpecialCategory && (
          <div className="np-field">
            <label>Article 9(2) condition <span className="text-danger">*</span></label>
            <RichTextField
              value={draft.specialCategoryCondition}
              onChange={(html) => update({ specialCategoryCondition: html })}
              placeholder="Which Article 9(2) condition permits this processing, and any supplementary national-law condition."
            />
            <div className="np-banner np-tone-danger" role="alert">
              <i className="ri-alert-line"></i>
              Special category data is in scope, so processing is prohibited unless an Article 9(2)
              condition applies. A lawful basis under Article 6 is not sufficient on its own.
            </div>
          </div>
        )}
      </SectionCard>

      {/* 3 — Data subjects */}
      <SectionCard
        title="Data subjects"
        icon="ri-group-line"
        hint="Article 30(1)(c) — whose data this is, and how many people"
      >
        <div className="np-field">
          <label htmlFor="pa-subjects">Categories of data subject <span className="text-danger">*</span></label>
          <Select
            inputId="pa-subjects"
            options={multiOptions(DATA_SUBJECT_CATEGORIES, DATA_SUBJECT_CATEGORY_LABELS)}
            value={selectedMulti(draft.dataSubjectCategories ?? [], DATA_SUBJECT_CATEGORY_LABELS)}
            onChange={(opts) =>
              update({ dataSubjectCategories: (opts as SelectOption[]).map((o) => o.value) })
            }
            placeholder="Whose personal data is processed?"
            isMulti
            isSearchable
            menuPortalTarget={document.body}
            styles={reactSelectStyles()}
          />
        </div>

        <div className="np-row2">
          <div className="np-field">
            <label htmlFor="pa-volume">Approximate number of data subjects</label>
            <select
              id="pa-volume"
              className="np-select"
              value={draft.dataSubjectVolume}
              onChange={(e) => update({ dataSubjectVolume: e.target.value })}
            >
              {SUBJECT_VOLUME_BANDS.map((b) => (
                <option key={b || 'unset'} value={b}>{SUBJECT_VOLUME_LABELS[b]}</option>
              ))}
            </select>
            <div className="np-hint">
              Banded on purpose — what matters is whether the processing is “large scale”, which is
              one of the DPIA screening criteria.
            </div>
          </div>
          <div className="np-field">
            <label htmlFor="pa-vulnerable">Vulnerable data subjects</label>
            <div className="np-switch">
              <input
                id="pa-vulnerable"
                type="checkbox"
                checked={draft.isVulnerableGroup}
                onChange={(e) => update({ isVulnerableGroup: e.target.checked })}
              />
              <span className="np-switch-track"></span>
              <span className="np-switch-thumb"></span>
            </div>
            {vulnerableFromCategories && !draft.isVulnerableGroup && (
              <div className="np-banner np-tone-warning" role="alert">
                <i className="ri-information-line"></i>
                One of the categories you selected is normally treated as vulnerable, where the
                imbalance of power means consent is rarely freely given. Consider turning this on.
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* 4 — Personal data */}
      <SectionCard
        title="Categories of personal data"
        icon="ri-file-list-3-line"
        hint="Article 30(1)(c) — what data, from where, and how sensitive"
      >
        {dataRows.rows.map((row, index) => (
          <RepeatableRowCard
            key={row.id}
            label="Data category"
            index={index}
            accent={(
              row.isSpecialCategory ? 'danger'
                : row.isPersonalData !== 'no' ? DATA_SENSITIVITY_TONE[row.isPersonalData]
                  : undefined
            ) as RowAccent | undefined}
            chips={
              <>
                {row.isPersonalData !== 'no' && (
                  <span className={`np-chip np-tone-${DATA_SENSITIVITY_TONE[row.isPersonalData]}`}>
                    {row.isPersonalData === 'yes' ? 'Personal data' : 'Personal data?'}
                  </span>
                )}
                {row.isSpecialCategory && (
                  <span className="np-chip np-tone-danger">
                    <i className="ri-alert-line"></i>Article 9
                  </span>
                )}
              </>
            }
            onRemove={dataRows.canRemove ? () => dataRows.remove(row.id) : undefined}
          >
            <div className="np-row2">
              <div className="np-field">
                <label>What data</label>
                <input
                  className="np-input"
                  value={row.name}
                  onChange={(e) => dataRows.patch(row.id, { name: e.target.value })}
                  placeholder="e.g. Name, CV, interview notes"
                />
              </div>
              <div className="np-field">
                <label>Source</label>
                <input
                  className="np-input"
                  value={row.dataSource}
                  onChange={(e) => dataRows.patch(row.id, { dataSource: e.target.value })}
                  placeholder="e.g. Provided by the candidate"
                />
              </div>
            </div>
            <div className="np-field">
              <label>Purpose of this data</label>
              <input
                className="np-input"
                value={row.processingPurpose}
                onChange={(e) => dataRows.patch(row.id, { processingPurpose: e.target.value })}
                placeholder="Why is this particular data needed?"
              />
            </div>
            <div className="np-row2">
              <div className="np-field">
                <label>Category</label>
                <select
                  className="np-select"
                  value={row.category}
                  onChange={(e) =>
                    dataRows.patch(row.id, { category: e.target.value as DataField['category'] })
                  }
                >
                  {DATA_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{DATA_CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div className="np-field">
                <label>Sensitivity</label>
                <select
                  className="np-select"
                  value={row.sensitivity}
                  onChange={(e) =>
                    dataRows.patch(row.id, {
                      sensitivity: e.target.value as DataField['sensitivity'],
                    })
                  }
                >
                  {SENSITIVITY_LEVELS.map((s) => (
                    <option key={s} value={s}>{SENSITIVITY_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="np-row2">
              <fieldset className="mb-0">
                <legend className="np-field-caption">Personal data?</legend>
                <div className="np-pill-toggle-group">
                  {DATA_SENSITIVITY_ANSWERS.map((answer) => (
                    <button
                      key={answer}
                      type="button"
                      className={`np-pill-toggle${row.isPersonalData === answer ? ` active np-tone-${DATA_SENSITIVITY_TONE[answer]}` : ''}`}
                      aria-pressed={row.isPersonalData === answer}
                      onClick={() =>
                        // Clearing personal data must clear the Article 9 flag too — the
                        // server does the same, and leaving them out of step would show a
                        // special category signal on non-personal data.
                        dataRows.patch(row.id, answer === 'no'
                          ? { isPersonalData: answer, isSpecialCategory: false }
                          : { isPersonalData: answer })
                      }
                    >
                      {DATA_SENSITIVITY_LABELS[answer]}
                    </button>
                  ))}
                </div>
                {DATA_SENSITIVITY_HINT[row.isPersonalData] && (
                  <p className="np-hint mt-1 mb-0">{DATA_SENSITIVITY_HINT[row.isPersonalData]}</p>
                )}
              </fieldset>

              {/* Special category is only asked once personal data is at least possible —
                  'customer_controlled' included, since a field's special-category status
                  can't be ruled out any more confidently than its personal-data status can.
                  Unlike personal data, this is a plain Yes/No — a single switch, not a
                  three-way pill group — since Article 9 status doesn't have a
                  "customer controlled" case of its own. */}
              {row.isPersonalData !== 'no' && (
                <fieldset className="mb-0">
                  <legend className="np-field-caption">Special category (Article 9)?</legend>
                  <div className="d-flex align-items-center gap-2">
                    <label className="np-switch mb-0" htmlFor={`pa-special-${row.id}`}>
                      <input
                        type="checkbox"
                        id={`pa-special-${row.id}`}
                        checked={row.isSpecialCategory}
                        onChange={(e) => dataRows.patch(row.id, { isSpecialCategory: e.target.checked })}
                      />
                      <span className="np-switch-track"></span>
                      <span className="np-switch-thumb"></span>
                    </label>
                    <label htmlFor={`pa-special-${row.id}`} className="mb-0 small">
                      Special category <span className="np-sub">(health, biometric…)</span>
                    </label>
                  </div>
                </fieldset>
              )}
            </div>
          </RepeatableRowCard>
        ))}
        <button
          className="np-btn np-btn-ghost np-btn-sm"
          onClick={() => dataRows.add()}
          disabled={!dataRows.canAdd}
        >
          <i className="ri-add-line"></i>Add a data category
        </button>
      </SectionCard>

      {/* 5 — Recipients */}
      <SectionCard
        title="Recipients"
        icon="ri-share-forward-line"
        hint="Article 30(1)(d) — who else sees this data"
      >
        <div className="np-field">
          <label htmlFor="pa-recipients">Categories of recipient</label>
          <Select
            inputId="pa-recipients"
            options={multiOptions(RECIPIENT_CATEGORIES, RECIPIENT_CATEGORY_LABELS)}
            value={selectedMulti(draft.recipientCategories ?? [], RECIPIENT_CATEGORY_LABELS)}
            onChange={(opts) =>
              update({ recipientCategories: (opts as SelectOption[]).map((o) => o.value) })
            }
            placeholder="Who receives this personal data?"
            isMulti
            isSearchable
            menuPortalTarget={document.body}
            styles={reactSelectStyles()}
          />
        </div>
        <div className="np-field">
          <label htmlFor="pa-processors">Processors</label>
          <textarea
            id="pa-processors"
            className="np-textarea"
            rows={3}
            value={(draft.processors ?? []).join('\n')}
            onChange={(e) =>
              update({ processors: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })
            }
            placeholder={'One per line, e.g.\nWorkday — HR system\nGreenhouse — applicant tracking'}
          />
          <div className="np-hint">
            Free text for now. There is no supplier register in Nimbus yet, so this cannot reference
            a processor record or its data processing agreement — one per line until it can.
          </div>
        </div>
      </SectionCard>

      {/* 6 — Transfers */}
      <SectionCard
        title="Transfers outside the EEA"
        icon="ri-global-line"
        hint="Article 30(1)(e) and Chapter V — leave empty if there are none"
      >
        {transferRows.rows.map((row, index) => (
          <RepeatableRowCard
            key={row.id}
            label="Transfer"
            index={index}
            accent={row.mechanism === 'none' ? 'danger' : 'success'}
            chips={
              row.country ? (
                <span className={`np-chip np-tone-${TRANSFER_MECHANISM_TONE[row.mechanism]}`}>
                  {TRANSFER_MECHANISM_LABELS[row.mechanism]}
                </span>
              ) : undefined
            }
            onRemove={transferRows.canRemove ? () => transferRows.remove(row.id) : undefined}
          >
            <div className="np-row2">
              <div className="np-field">
                <label>Country or territory</label>
                <input
                  className="np-input"
                  value={row.country}
                  onChange={(e) => transferRows.patch(row.id, { country: e.target.value })}
                  placeholder="e.g. United States"
                />
              </div>
              <div className="np-field">
                <label>Recipient</label>
                <input
                  className="np-input"
                  value={row.recipient}
                  onChange={(e) => transferRows.patch(row.id, { recipient: e.target.value })}
                  placeholder="Who receives it there?"
                />
              </div>
            </div>
            <div className="np-field">
              <label>Transfer mechanism</label>
              <select
                className="np-select"
                value={row.mechanism}
                onChange={(e) =>
                  transferRows.patch(row.id, { mechanism: e.target.value as TransferMechanism })
                }
              >
                {TRANSFER_MECHANISMS.map((m) => (
                  <option key={m} value={m}>{TRANSFER_MECHANISM_LABELS[m]}</option>
                ))}
              </select>
            </div>
            <div className="np-field">
              <label>Safeguards</label>
              <input
                className="np-input"
                value={row.safeguards}
                onChange={(e) => transferRows.patch(row.id, { safeguards: e.target.value })}
                placeholder="e.g. SCCs module 2 (2021), plus a transfer impact assessment dated 2026-03"
              />
            </div>
            {row.mechanism === 'none' && (row.country || row.recipient) && (
              <div className="np-banner np-tone-danger" role="alert">
                <i className="ri-error-warning-line"></i>
                This transfer has no Chapter V mechanism recorded. The activity cannot be activated
                until one is chosen — a transfer without a lawful basis is the most reportable state
                this record can be in.
              </div>
            )}
          </RepeatableRowCard>
        ))}
        <button
          className="np-btn np-btn-ghost np-btn-sm"
          onClick={() => transferRows.add()}
          disabled={!transferRows.canAdd}
        >
          <i className="ri-add-line"></i>Add a transfer
        </button>
      </SectionCard>

      {/* 7 — Retention */}
      <SectionCard
        title="Retention"
        icon="ri-time-line"
        hint="Article 30(1)(f) — time limits for erasure, per category of data"
      >
        {retentionRows.rows.map((row, index) => (
          <RepeatableRowCard
            key={row.id}
            label="Retention rule"
            index={index}
            onRemove={retentionRows.canRemove ? () => retentionRows.remove(row.id) : undefined}
          >
            <div className="np-row2">
              <div className="np-field">
                <label>Data category</label>
                <select
                  className="np-select"
                  value={row.dataCategory}
                  onChange={(e) =>
                    retentionRows.patch(row.id, {
                      dataCategory: e.target.value as RetentionRule['dataCategory'],
                    })
                  }
                >
                  {DATA_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{DATA_CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div className="np-field">
                <label>Retention period</label>
                <input
                  className="np-input"
                  value={row.period}
                  onChange={(e) => retentionRows.patch(row.id, { period: e.target.value })}
                  placeholder="e.g. 6 months"
                />
              </div>
            </div>
            <div className="np-field">
              <label>What starts the clock</label>
              <input
                className="np-input"
                value={row.trigger}
                onChange={(e) => retentionRows.patch(row.id, { trigger: e.target.value })}
                placeholder="e.g. From the date the vacancy is closed"
              />
            </div>
          </RepeatableRowCard>
        ))}
        <button
          className="np-btn np-btn-ghost np-btn-sm"
          onClick={() => retentionRows.add()}
          disabled={!retentionRows.canAdd}
        >
          <i className="ri-add-line"></i>Add a retention rule
        </button>
      </SectionCard>

      {/* 8 — Security */}
      <SectionCard
        title="Security measures"
        icon="ri-lock-line"
        hint="Article 30(1)(g) and Article 32"
      >
        <div className="np-field">
          <label>Security measures <span className="text-danger">*</span></label>
          <RichTextField
            value={draft.securityMeasures}
            onChange={(html) => update({ securityMeasures: html })}
            placeholder="A general description of the technical and organisational measures — access control, encryption, pseudonymisation, backups, testing."
            height={160}
          />
        </div>
      </SectionCard>
    </div>
  );
};

// ─── Route shell ──────────────────────────────────────────────────────────────

export const ProcessingActivityDetail: React.FC = () => {
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // /processing-activities/new is its own route, so the param is absent there; the explicit
  // 'new' check also covers the route being wired as a single :activityId pattern.
  const isNew = !activityId || activityId === 'new';
  const createStarted = useRef(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['processingActivity', activityId],
    queryFn: () => processingActivityApi.get(activityId!),
    enabled: !isNew && !!activityId,
  });

  const createMutation = useMutation({
    mutationFn: () => processingActivityApi.create({ name: '', role: 'controller' }),
    onSuccess: (created) => {
      queryClient.setQueryData(['processingActivity', created.activityId], created);
      queryClient.invalidateQueries({ queryKey: ['list', '/processing-activities'] });
      navigate(`/processing-activities/${created.activityId}`, { replace: true });
    },
    onError: (err) => {
      toast.error(describeApiError(err, 'Could not start a new processing activity.'));
      navigate('/processing-activities', { replace: true });
    },
  });

  useEffect(() => {
    if (!isNew || createStarted.current) return;
    createStarted.current = true; // StrictMode double-invokes effects — never mint two records
    createMutation.mutate();
    // createMutation is rebuilt each render; the ref above is the real guard.
  }, [isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isNew || isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <PageHeader
          icon="ri-database-2-line"
          title="Processing activity not found"
          subtitle={`Nothing could be loaded for ${activityId ?? 'this reference'}.`}
          breadcrumbs={[
            { label: 'Processing Activities', to: '/processing-activities' },
            { label: 'Not found' },
          ]}
          actions={
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/processing-activities')}>
              <i className="ri-arrow-left-line"></i>Back to register
            </button>
          }
        />
        <div className="np-banner np-tone-danger" role="alert">
          <i className="ri-error-warning-line"></i>
          This activity may have been deleted, or you may not have access to it.
        </div>
      </div>
    );
  }

  // Keyed on the record so switching records rebuilds the draft from scratch, while a
  // background refetch of the same record never clobbers work in progress.
  return <ActivityEditor key={data.activityId} record={data} />;
};

export default ProcessingActivityDetail;
